import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useI18n } from "@/context/LanguageContext";
import { fileToDataUrl, captureVideoFrame, captureVideoFrames, getVideoDuration } from "@/lib/media";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card } from "@/components/ui/card";
import CameraRecorder from "@/components/CameraRecorder";
import MusicPicker from "@/components/MusicPicker";
import { Upload, ImageIcon, Send, Lock, X, Users, Play, LayoutGrid, Music, Video, Hash, Volume2, Mic2, Image as ImageLucide } from "lucide-react";
import { euro, initials, compressionHint } from "@/lib/format";
import { toast } from "sonner";

const MIN = 1;
const MAX = 10000;
const SLIDER_MAX = 10000;
const MAX_VIDEO_SECONDS = 600; // 10 minutes

const SendPhoto = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [params] = useSearchParams();
  const fileRef = useRef();
  const [friends, setFriends] = useState([]);
  const [selected, setSelected] = useState(params.get("to") || "");
  const [media, setMedia] = useState(null);
  const [mediaType, setMediaType] = useState("photo");
  const [preview, setPreview] = useState(null);
  const [frames, setFrames] = useState([]);
  const [cover, setCover] = useState(null);
  const [price, setPrice] = useState(7.5);
  const [caption, setCaption] = useState("");
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [track, setTrack] = useState(null);
  const [soundVol, setSoundVol] = useState(0.8);
  const [origVol, setOrigVol] = useState(1);
  const [dest, setDest] = useState(params.get("to") ? "friend" : "public");
  const [busy, setBusy] = useState(false);
  const [recorderOpen, setRecorderOpen] = useState(false);
  const [musicOpen, setMusicOpen] = useState(false);

  useEffect(() => {
    api.get("/friends").then(({ data }) => setFriends(data.friends)).catch(() => {});
  }, []);

  const ingestFile = async (file) => {
    const isVideo = file.type.startsWith("video/");
    const isImage = file.type.startsWith("image/");
    if (!isVideo && !isImage) return toast.error("Veuillez choisir une image ou une vidéo");
    if (isImage && file.size > 8 * 1024 * 1024) return toast.error("Image trop volumineuse (max 8 Mo)");
    if (isVideo && file.size > 60 * 1024 * 1024) return toast.error("Vidéo trop volumineuse (max 60 Mo)");
    if (isVideo) {
      const dur = await getVideoDuration(file);
      if (dur && dur > MAX_VIDEO_SECONDS + 1) {
        return toast.error("Durée maximale : 10 minutes. Cette vidéo est trop longue.");
      }
    }
    const dataUrl = await fileToDataUrl(file);
    setMedia(dataUrl);
    if (isVideo) {
      setMediaType("video");
      const frame = await captureVideoFrame(file);
      setPreview(frame);
      setCover(frame);
      const fr = await captureVideoFrames(file, 6);
      setFrames(fr && fr.length ? fr : (frame ? [frame] : []));
    } else {
      setMediaType("photo");
      setPreview(null);
      setCover(null);
      setFrames([]);
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await ingestFile(file);
  };

  const onRecorded = async (file) => {
    setDest("public"); // recorded videos are public by default
    await ingestFile(file);
  };

  const setPriceSafe = (v) => {
    let n = Number(v);
    if (isNaN(n)) return;
    n = Math.max(MIN, Math.min(MAX, n));
    setPrice(Math.round(n * 2) / 2);
  };

  const clearMedia = () => {
    setMedia(null); setMediaType("photo"); setPreview(null); setCover(null); setFrames([]);
  };

  const addTag = (raw) => {
    const clean = (raw || "").replace(/[#\s]/g, "").toLowerCase().slice(0, 30);
    if (!clean) return;
    setTags((prev) => (prev.includes(clean) || prev.length >= 10 ? prev : [...prev, clean]));
    setTagInput("");
  };

  const onTagKey = (e) => {
    if (e.key === "Enter" || e.key === " " || e.key === ",") { e.preventDefault(); addTag(tagInput); }
  };

  const submit = async () => {
    if (!media) return toast.error("Ajoutez une photo ou vidéo");
    setBusy(true);
    try {
      if (dest === "public") {
        const { data: pub } = await api.post("/wall/publish", {
          image_base64: media,
          caption,
          media_type: mediaType,
          cover_base64: cover || preview || "",
          preview_base64: preview || "",
          hashtags: tags,
          sound: track ? `${track.title} · ${track.artist}` : "",
          sound_key: track?.key || "",
          sound_title: track?.title || "",
          sound_artist: track?.artist || "",
          sound_artwork_url: track?.artwork_url || "",
          sound_preview_url: track?.preview_url || "",
          sound_volume: soundVol,
          original_volume: origVol,
        });
        toast.success("Publié — gratuit et visible par tous", { description: compressionHint(pub?.video_info) || undefined });
        navigate(mediaType === "video" ? "/decouvrir" : "/mur");
      } else {
        if (!selected) { setBusy(false); return toast.error("Choisissez un destinataire"); }
        const { data: sent } = await api.post("/photos/send", {
          receiver_username: selected,
          image_base64: media,
          price: Number(price),
          caption,
          media_type: mediaType,
          preview_base64: preview || "",
          preview_frames: frames || [],
        });
        toast.success("Média verrouillé envoyé à @" + selected, { description: compressionHint(sent?.video_info) || undefined });
        navigate(`/messages/${selected}`);
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const isPublic = dest === "public";

  return (
    <div className="space-y-5">
      <h1 className="font-display text-2xl">{t("send.title")}</h1>

      {/* Destination toggle */}
      <div className="flex rounded-xl bg-secondary p-1" data-testid="publish-destination-toggle">
        <button
          onClick={() => setDest("public")}
          data-testid="dest-public"
          className={`flex-1 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-1.5 transition-colors ${isPublic ? "bg-card shadow-sm" : "text-muted-foreground"}`}
        >
          <LayoutGrid className="h-4 w-4" /> Public · Gratuit
        </button>
        <button
          onClick={() => setDest("friend")}
          data-testid="dest-friend"
          className={`flex-1 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-1.5 transition-colors ${!isPublic ? "bg-card shadow-sm" : "text-muted-foreground"}`}
        >
          <Lock className="h-4 w-4" /> Privé · Payant
        </button>
      </div>
      <p className="text-xs text-muted-foreground -mt-3">
        {isPublic
          ? "Publication gratuite, visible par tous dans le fil. Ajoutez musique, description et hashtags."
          : "Envoyé en privé à un ami — verrouillé jusqu'au paiement du destinataire."}
      </p>

      {/* Recipient (private mode only) */}
      {!isPublic && (
        <div className="space-y-2">
          <Label>{t("send.recipient")}</Label>
          {friends.length === 0 ? (
            <Card className="p-4 text-sm text-muted-foreground flex items-center justify-between rounded-xl">
              <span className="flex items-center gap-2"><Users className="h-4 w-4" /> {t("send.noFriends")}</span>
              <Button size="sm" variant="outline" className="rounded-xl" onClick={() => navigate("/amis")}>{t("send.add")}</Button>
            </Card>
          ) : (
            <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1" data-testid="send-photo-friend-list">
              {friends.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setSelected(f.username)}
                  data-testid={`send-photo-friend-${f.username}`}
                  className={`shrink-0 flex flex-col items-center gap-1 p-2 rounded-xl border transition-colors ${selected === f.username ? "border-primary bg-primary/5" : "border-border bg-card"}`}
                >
                  <Avatar className="h-11 w-11">
                    {f.avatar && <AvatarImage src={f.avatar} alt={f.username} />}
                    <AvatarFallback className="bg-accent text-accent-foreground text-xs font-semibold">{initials(f.username)}</AvatarFallback>
                  </Avatar>
                  <span className="text-[11px] max-w-[64px] truncate">@{f.username}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Upload / Record */}
      <div className="space-y-2">
        <Label>{t("send.media")}</Label>
        <input ref={fileRef} type="file" accept="image/*,video/*" className="hidden" onChange={onFile} data-testid="send-photo-file-input" />
        {media ? (
          <div className="relative rounded-2xl overflow-hidden border border-border">
            {mediaType === "video" ? (
              <video src={media} className="w-full aspect-[4/5] object-cover bg-black" controls playsInline data-testid="send-media-video-preview" />
            ) : (
              <img src={media} alt="aperçu" className="w-full aspect-[4/5] object-cover" />
            )}
            <button onClick={clearMedia} className="absolute top-3 right-3 h-8 w-8 rounded-lg bg-card/90 grid place-items-center" data-testid="send-photo-remove">
              <X className="h-4 w-4" />
            </button>
            <div className="absolute bottom-3 left-3 flex items-center gap-1 rounded-full bg-card/90 px-3 py-1 text-xs font-medium">
              {mediaType === "video" ? <Play className="h-3 w-3" /> : <Lock className="h-3 w-3" />} {isPublic ? "Gratuit" : `${t("send.willLock")} ${euro(price)}`}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => fileRef.current?.click()}
              className="aspect-[4/5] rounded-2xl border-2 border-dashed border-border bg-card grid place-items-center hover:border-primary transition-colors"
              data-testid="send-photo-upload-zone"
            >
              <div className="flex flex-col items-center gap-2 text-muted-foreground">
                <div className="h-12 w-12 rounded-2xl bg-secondary grid place-items-center"><ImageIcon className="h-6 w-6" /></div>
                <span className="text-sm font-medium">Importer</span>
                <span className="text-[11px]">Photo ou vidéo</span>
              </div>
            </button>
            <button
              onClick={() => setRecorderOpen(true)}
              className="aspect-[4/5] rounded-2xl border-2 border-dashed border-border bg-card grid place-items-center hover:border-primary transition-colors"
              data-testid="send-record-zone"
            >
              <div className="flex flex-col items-center gap-2 text-muted-foreground">
                <div className="h-12 w-12 rounded-2xl bg-secondary grid place-items-center"><Video className="h-6 w-6" /></div>
                <span className="text-sm font-medium">Enregistrer</span>
                <span className="text-[11px]">Max 10 min</span>
              </div>
            </button>
          </div>
        )}
        {media && (
          <Button variant="outline" className="w-full rounded-xl" onClick={() => fileRef.current?.click()}>
            <Upload className="h-4 w-4 mr-1" /> {t("send.changeMedia")}
          </Button>
        )}
      </div>

      {/* Cover picker (public video) */}
      {isPublic && mediaType === "video" && frames.length > 1 && (
        <div className="space-y-2">
          <Label className="flex items-center gap-1.5"><ImageLucide className="h-4 w-4" /> Miniature / couverture</Label>
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            {frames.map((f, i) => (
              <button
                key={i}
                onClick={() => setCover(f)}
                data-testid={`cover-frame-${i}`}
                className={`shrink-0 h-24 w-16 rounded-lg overflow-hidden border-2 transition-colors ${cover === f ? "border-primary" : "border-transparent"}`}
              >
                <img src={f} alt={`frame ${i}`} className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Sound (public only) */}
      {isPublic && (
        <div className="space-y-2">
          <Label className="flex items-center gap-1.5"><Music className="h-4 w-4" /> Son / musique</Label>
          {track ? (
            <div className="flex items-center gap-3 rounded-xl border border-border p-2" data-testid="selected-sound">
              {track.artwork_url && <img src={track.artwork_url} alt="" className="h-11 w-11 rounded-lg object-cover" />}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{track.title}</p>
                <p className="text-xs text-muted-foreground truncate">{track.artist}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setMusicOpen(true)} data-testid="change-sound">Changer</Button>
              <button onClick={() => setTrack(null)} className="h-7 w-7 grid place-items-center rounded-lg hover:bg-secondary" aria-label="Retirer le son"><X className="h-4 w-4" /></button>
            </div>
          ) : (
            <Button variant="outline" className="w-full rounded-xl" onClick={() => setMusicOpen(true)} data-testid="open-music-picker">
              <Music className="h-4 w-4 mr-1" /> Choisir un son dans la bibliothèque
            </Button>
          )}
          {/* Volume mixing for videos with a sound */}
          {track && mediaType === "video" && (
            <div className="space-y-3 rounded-xl bg-secondary/50 p-3">
              <div>
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-1"><span className="flex items-center gap-1"><Music className="h-3.5 w-3.5" /> Volume musique</span><span>{Math.round(soundVol * 100)}%</span></div>
                <Slider min={0} max={1} step={0.05} value={[soundVol]} onValueChange={(v) => setSoundVol(v[0])} data-testid="sound-volume-slider" />
              </div>
              <div>
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-1"><span className="flex items-center gap-1"><Mic2 className="h-3.5 w-3.5" /> Volume vidéo (son original)</span><span>{Math.round(origVol * 100)}%</span></div>
                <Slider min={0} max={1} step={0.05} value={[origVol]} onValueChange={(v) => setOrigVol(v[0])} data-testid="orig-volume-slider" />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Price (private only) */}
      {!isPublic && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label>{t("send.price")}</Label>
            <div className="flex items-center gap-2">
              <Input type="number" min={MIN} max={MAX} step={0.5} value={price} onChange={(e) => setPriceSafe(e.target.value)} className="w-28 rounded-xl text-right" data-testid="send-photo-price-input" />
              <span className="text-sm text-muted-foreground">€</span>
            </div>
          </div>
          <Slider min={MIN} max={SLIDER_MAX} step={0.5} value={[Math.min(price, SLIDER_MAX)]} onValueChange={(v) => setPrice(v[0])} data-testid="send-photo-price-slider" />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>1 € – 10 000 €</span>
            <span>{t("send.youEarn")} <strong className="text-foreground">{euro(price * 0.8)}</strong> (80 %)</span>
          </div>
        </div>
      )}

      {/* Description */}
      <div className="space-y-2">
        <Label>{t("send.caption")}</Label>
        <Textarea value={caption} onChange={(e) => setCaption(e.target.value)} placeholder={isPublic ? "Décrivez votre vidéo…" : t("send.captionPlaceholder")} className="rounded-xl" data-testid="send-photo-caption" maxLength={280} />
      </div>

      {/* Hashtags (public) */}
      {isPublic && (
        <div className="space-y-2">
          <Label className="flex items-center gap-1.5"><Hash className="h-4 w-4" /> Hashtags</Label>
          <div className="relative">
            <Hash className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
            <Input
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={onTagKey}
              onBlur={() => tagInput && addTag(tagInput)}
              placeholder="Ajoutez un hashtag puis Entrée"
              className="pl-9 rounded-xl"
              maxLength={30}
              data-testid="hashtag-input"
            />
          </div>
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="hashtag-chips">
              {tags.map((tg) => (
                <span key={tg} className="flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2.5 py-1 text-xs font-medium">
                  #{tg}
                  <button onClick={() => setTags(tags.filter((x) => x !== tg))} aria-label={`Retirer ${tg}`}><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      <Button className="w-full rounded-xl h-12" onClick={submit} disabled={busy} data-testid="send-photo-submit-button">
        <Send className="h-4 w-4 mr-2" /> {busy ? t("send.sending") : (isPublic ? "Publier (gratuit)" : t("send.submit"))}
      </Button>

      <CameraRecorder open={recorderOpen} onOpenChange={setRecorderOpen} onRecorded={onRecorded} />
      <MusicPicker open={musicOpen} onOpenChange={setMusicOpen} onSelect={setTrack} />
    </div>
  );
};

export default SendPhoto;
